import { Box, Portal, Select, Text, createListCollection } from "@chakra-ui/react"
import { useState } from "react";
import { useChatbotContext } from "./chatbot-context";
import { useColorModeValue } from "./color-mode";
import { useAccount } from "./account-context";

/**
 * Props for the ChatbotSelect component.
 */
interface ChatbotSelectProps {
    /** The font family to use for labels and menu items. */
    fontFamily: string,
    /** Additional props to be passed to the Select.Root component. */
    [key: string]: any;
}

/**
 * Represents an item in the select menu collection.
 */
interface CollectionItem {
    /** The display label for the item. */
    label: string;
    /** The internal value for the item. */
    value: string;
}

/**
 * A dropdown component that allows users to select a chatbot agent.
 * Fetches the list of allowed bots from the account context and updates the global chatbot selection.
 * 
 * @param {ChatbotSelectProps} props - The component props.
 * @returns {JSX.Element} The rendered chatbot selection dropdown.
 */
export const ChatbotSelect = ({ fontFamily, ...rest }: ChatbotSelectProps) => {

    const { selectedChatbotValue, setSelectedChatbotValue } = useChatbotContext();
    const { allowedBots } = useAccount();

    /**
     * Handles the selection change event from the Chakra UI Select component.
     * Converts the string value back to a numeric index for the context.
     */
    const handleValueChange = (e: { value: string[] }) => {
        const selectedString = e.value[0]; // Get the first (and only) selected string value

        if (selectedString !== undefined) {
            // Convert the string index back to a number and update the context
            setSelectedChatbotValue(parseInt(selectedString));
        }
    };

    // Transform allowedBots into a collection format required by Chakra's Select
    const itemsForCollection: CollectionItem[] = allowedBots
        ? allowedBots.map((chatbot, index) => ({
            label: chatbot.subtitle,
            value: index.toString(),
        })): [];
    const chatbots = createListCollection({ items: itemsForCollection });


    return (
        <Select.Root variant={useColorModeValue('subtle', 'outline')} fontFamily={fontFamily} {...rest} collection={chatbots} onValueChange={handleValueChange} value={[selectedChatbotValue.toString()]} ml={'24px'} mt={'14px'} size="sm" width="240px">
            <Select.HiddenSelect />
            <Select.Label></Select.Label>
            <Select.Control>
                <Select.Trigger>
                    <Select.ValueText placeholder="Select chatbot" />
                </Select.Trigger>
                <Select.IndicatorGroup>
                    <Select.Indicator />
                </Select.IndicatorGroup>
            </Select.Control>
            <Portal>
                <Select.Positioner zIndex={"1401"}>
                    <Select.Content>
                        {chatbots.items.map((chatbot, index) => {
                            if (index === 0) {
                                return (
                                    <Select.Item fontFamily={fontFamily} item={chatbot} key={chatbot.value}>
                                        <Box flexDir={'column'}>
                                            <Text fontWeight={'bold'}>NEW</Text>
                                            {chatbot.label}
                                        </Box>
                                        <Select.ItemIndicator />
                                    </Select.Item>
                                )
                            }
                            else {
                                return (
                                    <Select.Item fontFamily={fontFamily} item={chatbot} key={chatbot.value}>
                                        {chatbot.label}
                                        <Select.ItemIndicator />
                                    </Select.Item>
                                )
                            }
                        })}
                    </Select.Content>
                </Select.Positioner>
            </Portal>
        </Select.Root>
    )
}
